<script setup lang="ts">
import AccordionTriangle from '~/components/icons/AccordionTriangle.vue';

type Px = string | number;

const props = withDefaults(
  defineProps<{
    /** Controlled through a v-model */
    modelValue?: boolean;
    /** Chip padding top */
    chipTop?: Px;
    /** Chip padding left */
    chipLeft?: Px;
    /** Chip width. If not set, it will be set to auto */
    chipWidth?: Px;
    /** Chip height. If not set, it will be set to auto */
    chipHeight?: Px;
    /** Internal chip padding */
    chipPadding?: Px;
    /** Chip border radius */
    chipRadius?: Px;
    /** Panel border radius */
    panelRadius?: Px;

    /** Panel top margin */
    panelInsetTop?: Px;
    /** Panel bottom margin */
    panelInsetBottom?: Px;
    /** Panel left/right margin */
    panelInsetSides?: Px;

    /** Panel padding top */
    panelPadTop?: Px;
    /** Panel padding right */
    panelPadRight?: Px;
    /** Panel padding bottom */
    panelPadBottom?: Px;
    /** Panel padding left */
    panelPadLeft?: Px;

    /** Animation duration in milliseconds */
    duration?: number;
    /** Cubic-bezier easing function */
    easing?: string;
    /** Panel z-index while opening */
    zIndex?: number;

    static?: boolean;
  }>(),
  {
    modelValue: undefined,
    chipTop: 16,
    chipLeft: 16,
    chipWidth: undefined,
    chipHeight: 44,
    chipPadding: 10,
    chipRadius: 12,
    panelRadius: 18,

    panelInsetTop: 24,
    panelInsetBottom: 24,
    panelInsetSides: 24,

    panelPadTop: 14,
    panelPadRight: 16,
    panelPadBottom: 14,
    panelPadLeft: 16,

    duration: 420,
    easing: 'cubic-bezier(.2,.8,.2,1)',
    zIndex: 20
  }
);

const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>();

const internalOpen = ref(false);
const open = computed({
  get: () => props.modelValue ?? internalOpen.value,
  set: (v) => (props.modelValue === undefined ? (internalOpen.value = v) : emit('update:modelValue', v))
});

const chip = ref<HTMLButtonElement | null>(null);
const panel = ref<HTMLDivElement | null>(null);
const card = ref<HTMLDivElement | null>(null);
const cardHovered = ref<boolean>(false);

const toPx = (v: Px) => (typeof v === 'number' ? `${v}px` : v);

const chipStyle = computed(
  () =>
    ({
      position: 'absolute',
      top: toPx(props.chipTop),
      left: toPx(props.chipLeft),
      width: props.chipWidth !== undefined ? toPx(props.chipWidth) : 'auto',
      ['--ea-chip-right']: toPx(props.chipLeft),
      height: toPx(props.chipHeight),
      padding: toPx(props.chipPadding),
      borderRadius: toPx(props.chipRadius)
    }) as Record<string, string>
);

const panelStyle = computed(
  () =>
    ({
      position: 'absolute',
      inset: `${toPx(props.panelInsetTop)} ${toPx(props.panelInsetSides)} ${toPx(props.panelInsetBottom)} ${toPx(props.panelInsetSides)}`,
      zIndex: props.zIndex!.toString(),
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      height: '100%',
      maxHeight: `calc(100% - ${props.panelInsetTop ?? 0}px - ${props.panelInsetBottom ?? 0}px)`
    }) as Record<string, string>
);

const panelCardStyle = computed(
  () =>
    ({
      width: '100%',
      height: 'fit-content',
      maxHeight: '100%',
      borderRadius: toPx(props.panelRadius),
      padding: `${toPx(props.panelPadTop)} ${toPx(props.panelPadRight)} ${toPx(props.panelPadBottom)} ${toPx(props.panelPadLeft)}`
    }) as Record<string, string>
);

function animateFLIP(type: 'enter' | 'leave', done: () => void) {
  if (!chip.value || !card.value) return done();

  const chipRect = chip.value.getBoundingClientRect();
  const cardRect = card.value.getBoundingClientRect();

  const dx = chipRect.left - cardRect.left;
  const dy = chipRect.top - cardRect.top;
  const sx = chipRect.width / cardRect.width;
  const sy = chipRect.height / cardRect.height;

  const keyframes =
    type === 'enter'
      ? [
          { transform: `translate(${dx}px,${dy}px) scale(${sx},${sy})`, opacity: 0 },
          { transform: 'translate(0,0) scale(1,1)', opacity: 1 }
        ]
      : [
          { transform: 'translate(0,0) scale(1,1)', opacity: 1 },
          { transform: `translate(${dx}px,${dy}px) scale(${sx},${sy})`, opacity: 0 }
        ];

  const anim = card.value.animate(keyframes, {
    duration: props.duration,
    easing: props.easing,
    fill: 'both'
  });

  // Fade chip in/out in sync
  let chipAnim: Animation | undefined;
  if (chip.value) {
    chipAnim = chip.value.animate(
      type === 'enter' ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 1 }],
      {
        duration: props.duration,
        easing: props.easing,
        fill: 'both'
      }
    );
    // Disable clicks while invisible
    if (type === 'enter') chip.value.style.pointerEvents = 'none';
  }

  let backdropAnim: Animation | undefined;
  if (panel.value) {
    panel.value.style.setProperty('--_panel-bg-from', type === 'enter' ? '0' : '1');
    panel.value.style.setProperty('--_panel-bg-to', type === 'enter' ? '1' : '0');
    backdropAnim = panel.value.animate([{ opacity: type === 'enter' ? 0 : 1 }, { opacity: type === 'enter' ? 1 : 0 }], {
      duration: props.duration,
      easing: props.easing,
      fill: 'both'
    });
  }

  const finalize = () => {
    backdropAnim?.finish();
    chipAnim?.finish();
    if (chip.value) {
      if (type === 'leave') {
        chip.value.style.pointerEvents = '';
        chip.value.style.opacity = '1';
      } else {
        // ensure chip fully hidden
        chip.value.style.opacity = '0';
      }
    }
    done();
  };

  anim.addEventListener('finish', finalize);
  anim.addEventListener('cancel', finalize);
}

function onEnter(_: Element, done: () => void) {
  // Waiting for card to render and then run flip animation
  requestAnimationFrame(() => animateFLIP('enter', done));
}
function onLeave(el: Element, done: () => void) {
  const panelEl = el as HTMLElement;
  const cardEl = card.value ?? (panelEl.querySelector('.ea-card') as HTMLDivElement | null);
  const chipEl = chip.value;
  if (!chipEl || !cardEl) return done();
  // Lock the layout snapshot before the next frame
  const chipRect = chipEl.getBoundingClientRect();
  const cardRect = cardEl.getBoundingClientRect();

  // Run flip manually using web animation api
  const dx = chipRect.left - cardRect.left;
  const dy = chipRect.top - cardRect.top;
  const sx = chipRect.width / cardRect.width;
  const sy = chipRect.height / cardRect.height;

  const anim = cardEl.animate(
    [
      { transform: 'translate(0,0) scale(1,1)', opacity: 1 },
      { transform: `translate(${dx}px,${dy}px) scale(${sx},${sy})`, opacity: 0 }
    ],
    {
      duration: props.duration,
      easing: props.easing,
      fill: 'both'
    }
  );

  // Chip fade-in back
  const chipAnim: Animation | undefined = chipEl.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: props.duration,
    easing: props.easing,
    fill: 'both'
  });

  // Backdrop fade out
  let backdropAnim: Animation | undefined;
  if (panel.value) {
    panel.value.style.setProperty('--_panel-bg-from', '1');
    panel.value.style.setProperty('--_panel-bg-to', '0');
    backdropAnim = panel.value.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: props.duration,
      easing: props.easing,
      fill: 'both'
    });
  }

  const finalize = () => {
    backdropAnim?.finish();
    chipAnim?.finish();
    if (chip.value) {
      chip.value.style.pointerEvents = '';
      chip.value.style.opacity = '';
      chip.value.style.removeProperty('opacity');
    }
    done();
  };
  anim.addEventListener('finish', finalize);
  anim.addEventListener('cancel', finalize);
}

onMounted(() => {
  watch(
    open,
    (v) => {
      if (v) setTimeout(() => card.value?.focus(), 0);
    },
    { immediate: true }
  );
});
</script>

<template>
  <div class="ea-root">
    <button
      v-if="!static"
      ref="chip"
      class="ea-chip"
      :style="chipStyle"
      type="button"
      :aria-hidden="open ? 'true' : 'false'"
      :tabindex="open ? -1 : 0"
      :disabled="open"
      @click="!open && (open = true)"
    >
      <slot name="chip">Accordion</slot>
      <AccordionTriangle :size="14" :open="open" />
    </button>

    <Transition :css="false" @enter="onEnter" @leave="onLeave">
      <div
        v-if="open"
        ref="panel"
        class="ea-panel"
        :style="panelStyle"
        @keydown.esc.prevent.stop="open = false"
        @click.self="open = false"
      >
        <div
          ref="card"
          class="ea-card"
          :data-static="static"
          :style="panelCardStyle"
          tabindex="-1"
          role="dialog"
          aria-modal="true"
          @mouseenter="cardHovered = true"
          @mouseleave="cardHovered = false"
          @click="open = false"
        >
          <header class="ea-header">
            <div class="ea-title">
              <slot name="title">Дикий Лес</slot>
              <AccordionTriangle v-if="!static" :size="14" :open="cardHovered" />
            </div>
          </header>

          <div class="ea-content">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.ea-root {
  position: relative;
  --ea-chip-right: 16px;
  display: block;
  min-height: 1px;
  height: 100%;
}

.ea-chip {
  display: inline-flex;
  min-width: max-content;
  align-items: center;
  gap: 0.5rem;
  background: rgba(17, 35, 56, 0.72);
  color: #9ec7d4;
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  cursor: pointer;
  user-select: none;
  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition:
    transform 0.15s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    border-width 0.2s ease,
    color 0.2s ease;
  font-weight: 600;
  will-change: opacity, transform;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, #0083ad 0%, rgba(0, 0, 0, 0) 100%),
      linear-gradient(0deg, rgba(13, 36, 47, 0.75), rgba(13, 36, 47, 0.75)), #ffffff;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: -1;
    border: 3px solid #608897;
    border-radius: inherit;
  }
}
.ea-chip:hover {
  transform: translateY(-1px);
  color: #ffffff;
  box-shadow:
    0 10px 22px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);

  &::before {
    opacity: 1;
  }
}

.ea-card {
  position: relative;
  border: 1px solid #1e353e;
  color: #e6eef7;
  box-shadow:
    0 18px 60px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  outline: none;
  transform-origin: top left;
  overflow: auto;
  cursor: pointer;
  background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.5) 0%, rgba(0, 0, 0, 0) 100%)
    rgba(13, 36, 47, 0.85);

  &[data-static] {
    cursor: default;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  &::before {
    opacity: 0;
    background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.5) 0%, rgba(0, 0, 0, 0) 100%) #0d242f;
  }

  &::after {
    opacity: 0;
    background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.85) 0%, rgba(0, 0, 0, 0) 100%)
      #0d242f;
  }
}
.ea-card:not([data-static]):hover::before {
  opacity: 0.75;
}
.ea-card:not([data-static]):hover::after {
  opacity: 0;
}

.ea-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: center;
  text-align: center;
  margin-bottom: 0.5rem;
}
.ea-title {
  font-size: clamp(18px, 2.1vw, 20px);
  font-weight: 700;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}
.ea-close {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
}
.ea-content {
  line-height: 1.55;
  font-size: clamp(14px, 1.4vw, 16px);
  opacity: 0.98;
}
@media (max-width: 640px) {
  .ea-chip {
    right: var(--ea-chip-right, 16px);
    min-width: 0;
  }
}
</style>
